import { ChangeEvent, forwardRef, useImperativeHandle, useRef } from "react";
import { SecondaryButton } from "../../elements";
import styles from "./file-upload.module.less";
import { useTranslation } from "react-i18next";
import { isArrayWithItems } from "../../../helpers";
import InputElementTopSection from "../components/input-element-top-section/input-element-top-section";

export type TFileUploadProps = {
  name?: string;
  label?: string;
  value?: File | Array<File>;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  helperText?: string;
  errorText?: string;
  accept?: string;
  isDisabled?: boolean;
  allowMultipleFiles?: boolean;
};

const FileUpload = forwardRef<Partial<HTMLInputElement>, TFileUploadProps>(
  ({ name, label, value, helperText, errorText, onChange, accept, isDisabled, allowMultipleFiles }, forwardedRef) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(forwardedRef, () => {
      return {
        click() {
          fileInputRef.current?.click();
        },
        focus() {
          buttonRef.current?.focus();
        },
      };
    }, []);

    const onClickButton = () => {
      if (fileInputRef) {
        fileInputRef.current?.click();
      }
    };

    let valueText: React.ReactNode = "";
    if (value instanceof File) {
      valueText = value.name;
    } else if (isArrayWithItems(value)) {
      valueText = value.map((file, index) => <span key={index}>{file.name}</span>);
    } else {
      valueText = t("NO_FILE_SELECTED");
    }

    return (
      <div className={"space-mb-md"}>
        <InputElementTopSection label={label} helperText={helperText} errorText={errorText} htmlFor={name} />
        <div className={styles.buttonWrapper}>
          <SecondaryButton ref={buttonRef} label={t("UPLOAD")} onClick={onClickButton} isDisabled={isDisabled} />
          <p className={`${styles.fileName} body-s mb-0`}>{valueText}</p>
        </div>
        <input
          ref={fileInputRef}
          name={name}
          type="file"
          hidden
          onChange={onChange}
          accept={accept}
          multiple={allowMultipleFiles ? true : undefined}
          disabled={isDisabled ? true : undefined}
        />
      </div>
    );
  },
);

FileUpload.displayName = "FileUpload";

export default FileUpload;
